/**
 * Wishlist Storage & Main Entry Point
 * Handles localStorage, API sync, and global wishlist initialization
 */

class WishlistStorage {
  constructor() {
    this.LOCAL_STORAGE_KEY = 'shopify-wishlist';
    this.LOCAL_STORAGE_DELIMITER = ',';
    this.SERVICE_API_URL = 'https://rice-service.herokuapp.com';
    
    // Use global wishlist config
    const config = window.wishlistConfig || {};
    this.isLoggedIn = config.isLoggedIn || false;
    this.accountWishlist = config.accountWishlist || null;
    this.customer = config.customer || null;
  }

  /**
   * Initialize global wishlist system
   * This is called automatically when the script loads
   */
  static initialize() {
    // Create global storage instance
    window.wishlistStorage = new WishlistStorage();

    // Dispatch initialization event for components to listen to
    document.dispatchEvent(new CustomEvent('wishlist:storage-ready', {
      detail: { storage: window.wishlistStorage }
    }));

    // Initialize counter and buttons on page
    WishlistStorage.initializePageElements();
  }

  /**
   * Initialize existing elements on the page
   */
  static initializePageElements() {
    // Wait for storage to be available before initializing
    WishlistStorage.ensureStorageReady(() => {
      // Initialize counter
      WishlistStorage.updateCounter();

      // Initialize any existing wishlist buttons
      const buttons = document.querySelectorAll('wishlist-button');
      buttons.forEach(button => {
        if (button.updateButtonState) {
          button.updateButtonState();
        }
      });

      // Initialize clear all button
      WishlistStorage.initializeClearAllButton();
    });
  }

  /**
   * Initialize the clear all button with proper event handling
   */
  static initializeClearAllButton() {
    const clearButton = document.querySelector('[data-action="clear-wishlist"]');
    if (clearButton && !clearButton.hasAttribute('data-wishlist-initialized')) {
      clearButton.addEventListener('click', WishlistStorage.handleClearAllClick);
      clearButton.setAttribute('data-wishlist-initialized', 'true');
    }
  }

  /**
   * Handle clear all button click with confirmation
   */
  static async handleClearAllClick(event) {
    const button = event.currentTarget;
    const confirmMessage = button.getAttribute('data-confirm');
    
    // Show confirmation dialog
    if (confirmMessage && !confirm(confirmMessage)) {
      return; // User cancelled
    }

    // Find the wishlist grid and clear it
    const wishlistGrid = document.querySelector('wishlist-grid');
    if (wishlistGrid && wishlistGrid.clearAll) {
      try {
        await wishlistGrid.clearAll();
      } catch (error) {
        console.error('[WishlistStorage] Error clearing wishlist:', error);
      }
    }
  }

  /**
   * Ensure storage is ready before executing callback
   */
  static ensureStorageReady(callback) {
    if (window.wishlistStorage) {
      callback();
    } else {
      // Listen for storage ready event
      document.addEventListener('wishlist:storage-ready', callback, { once: true });
    }
  }

  /**
   * Update wishlist counter display
   */
  static updateCounter(count = null) {
    const counters = document.querySelectorAll('[data-wishlist-counter]');
    const wrappers = document.querySelectorAll('[data-wishlist-counter-wrapper]');
    
    if (counters.length === 0 || wrappers.length === 0 || !window.wishlistStorage) return;

    const wishlistCount = count !== null ? count : window.wishlistStorage.getWishlist().length;

    // Update all counter elements (header + mobile drawer)
    counters.forEach(counter => {
      if (wishlistCount > 0) {
        counter.textContent = wishlistCount;
      } else {
        counter.textContent = '';
      }
    });

    // Update all wrapper elements
    wrappers.forEach(wrapper => {
      if (wishlistCount > 0) {
        wrapper.classList.add('is-visible');
      } else {
        wrapper.classList.remove('is-visible');
      }
    });
  }

  /**
   * Show notification message
   */
  static showNotification(message) {
    const notice = document.querySelector('[data-wishlist-notice]');
    if (!notice) return;

    notice.textContent = message;
    const wrapper = notice.closest('[data-wishlist-notice-wrapper]');
    
    if (wrapper) {
      wrapper.classList.add('is-visible');
      setTimeout(() => {
        wrapper.classList.remove('is-visible');
      }, 2000);
    }
  }

  /**
   * Get current wishlist from localStorage or account
   * @returns {Array} Array of product IDs
   */
  getWishlist() {
    if (this.isLoggedIn && this.accountWishlist) {
      return this.accountWishlist.split(this.LOCAL_STORAGE_DELIMITER).filter(id => id);
    }
    
    const localWishlist = localStorage.getItem(this.LOCAL_STORAGE_KEY);
    return localWishlist ? localWishlist.split(this.LOCAL_STORAGE_DELIMITER).filter(id => id) : [];
  }

  /**
   * Set wishlist and sync to appropriate storage
   * @param {Array} wishlistArray Array of product IDs
   * @returns {Promise}
   */
  async setWishlist(wishlistArray) {
    const wishlistString = wishlistArray.join(this.LOCAL_STORAGE_DELIMITER);
    
    if (this.isLoggedIn && this.customer) {
      return this.updateCustomerWishlist(wishlistString);
    } else {
      if (wishlistArray.length > 0) {
        localStorage.setItem(this.LOCAL_STORAGE_KEY, wishlistString);
      } else {
        localStorage.removeItem(this.LOCAL_STORAGE_KEY);
      }
      return Promise.resolve(wishlistString);
    }
  }

  /**
   * Check if product ID exists in wishlist
   * @param {string} productId Product ID to check
   * @returns {boolean}
   */
  contains(productId) {
    const wishlist = this.getWishlist();
    return wishlist.includes(productId.toString());
  }

  /**
   * Add product to wishlist
   * @param {string} productId Product ID to add
   * @returns {Promise<Array>} Updated wishlist
   */
  async addToWishlist(productId) {
    const wishlist = this.getWishlist();
    if (!wishlist.includes(productId.toString())) {
      wishlist.push(productId.toString());
      await this.setWishlist(wishlist);
    }
    return wishlist;
  }

  /**
   * Remove product from wishlist
   * @param {string} productId Product ID to remove
   * @returns {Promise<Array>} Updated wishlist
   */
  async removeFromWishlist(productId) {
    const wishlist = this.getWishlist();
    const index = wishlist.indexOf(productId.toString());
    if (index > -1) {
      wishlist.splice(index, 1);
      await this.setWishlist(wishlist);
    }
    return wishlist;
  }

  /**
   * Toggle product in wishlist
   * @param {string} productId Product ID to toggle
   * @returns {Promise<{wishlist: Array, added: boolean}>}
   */
  async toggleProduct(productId) {
    const wishlist = this.getWishlist();
    const index = wishlist.indexOf(productId.toString());
    const added = index === -1;
    
    if (added) {
      wishlist.push(productId.toString());
    } else {
      wishlist.splice(index, 1);
    }
    
    await this.setWishlist(wishlist);
    return { wishlist, added };
  }

  /**
   * Merge local wishlist with account wishlist (for login scenarios)
   * @returns {Promise<Array>} Merged wishlist
   */
  async mergeWishlist() {
    const localWishlist = localStorage.getItem(this.LOCAL_STORAGE_KEY);
    let mainWishlist = this.accountWishlist ? this.accountWishlist.split(this.LOCAL_STORAGE_DELIMITER).filter(id => id) : [];
    
    if (localWishlist) {
      const localItems = localWishlist.split(this.LOCAL_STORAGE_DELIMITER).filter(id => id);
      mainWishlist = [...new Set([...mainWishlist, ...localItems])]; // Remove duplicates
      
      // Update customer metafield and clear local wishlist
      await this.updateCustomerWishlist(mainWishlist.join(this.LOCAL_STORAGE_DELIMITER));
      localStorage.removeItem(this.LOCAL_STORAGE_KEY);
    }
    
    return mainWishlist;
  }

  /**
   * Update customer wishlist via API
   * @param {string} wishlistString Comma-separated product IDs
   * @returns {Promise}
   */
  async updateCustomerWishlist(wishlistString) {
    if (!this.SERVICE_API_URL || !this.customer) {
      return Promise.reject('API URL or customer not available');
    }

    try {
      const response = await fetch(`${this.SERVICE_API_URL}/frontend/favorite`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: this.customer.id,
          email: this.customer.email,
          key: 'wishlist',
          value: wishlistString,
          namespace: 'rice'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response;
    } catch (error) {
      console.error('Failed to update customer wishlist:', error);
      throw error;
    }
  }

  /**
   * Clear entire wishlist
   * @returns {Promise<Array>} Empty array
   */
  async clearWishlist() {
    await this.setWishlist([]);
    return [];
  }

  /**
   * Get search query string for Shopify search
   * @param {Array} productIds Array of product IDs
   * @returns {string} Search query string
   */
  getSearchQueryString(productIds = null) {
    const ids = productIds || this.getWishlist();
    return ids.map(id => `id:${id}`).join(' OR ');
  }
}

// Export for use in other components
window.WishlistStorage = WishlistStorage;

/**
 * Wishlist Button Web Component
 */
class WishlistButton extends HTMLElement {
  constructor() {
    super();
    this.productId = this.getAttribute('data-product-id');
  }

  connectedCallback() {
    if (!this.productId) {
      console.error('[Wishlist Button] Missing data-product-id attribute');
      return;
    }

    // Wait for storage to be available
    WishlistStorage.ensureStorageReady(() => {
      this.updateButtonState();
      this.addEventListener('click', this.handleClick.bind(this));
      document.addEventListener('wishlist:updated', this.handleWishlistUpdate.bind(this));
    });
  }

  disconnectedCallback() {
    document.removeEventListener('wishlist:updated', this.handleWishlistUpdate.bind(this));
  }


  async handleClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.hasAttribute('disabled') || !window.wishlistStorage) return;

    this.setAttribute('disabled', '');
    
    try {
      const result = await window.wishlistStorage.toggleProduct(this.productId);
      
      // Update all buttons with same product ID
      this.updateAllButtonsForProduct(this.productId, result.added);
      
      // Update counter
      WishlistStorage.updateCounter(result.wishlist.length);
      
      // Show notification
      const config = window.wishlistConfig || {};
      const message = result.added ? 
        config.strings?.addedState || 'Added as favorite!' : 
        config.strings?.removedState || 'Removed from favorites!';
      WishlistStorage.showNotification(message);
      
      // Dispatch events
      document.dispatchEvent(new CustomEvent('wishlist:updated', {
        detail: { productId: this.productId, added: result.added, wishlist: result.wishlist }
      }));
      
      if (!result.added) {
        document.dispatchEvent(new CustomEvent('wishlist:product-removed', {
          detail: { productId: this.productId }
        }));
      }
      
    } catch (error) {
      console.error('[Wishlist Button] Error toggling wishlist:', error);
    } finally {
      this.removeAttribute('disabled');
    }
  }

  handleWishlistUpdate(event) {
    if (event.detail.productId === this.productId) {
      this.updateButtonState(event.detail.added);
    }
  }

  updateButtonState(isInWishlist = null) {
    if (!window.wishlistStorage) return;
    
    const inWishlist = isInWishlist !== null ? isInWishlist : window.wishlistStorage.contains(this.productId);
    
    if (inWishlist) {
      this.classList.add('active');
      this.setAttribute('aria-pressed', 'true');
      this.setAttribute('title', 'Remove from wishlist');
    } else {
      this.classList.remove('active');
      this.setAttribute('aria-pressed', 'false');
      this.setAttribute('title', 'Add to wishlist');
    }
  }

  updateAllButtonsForProduct(productId, isActive) {
    const buttons = document.querySelectorAll(`wishlist-button[data-product-id="${productId}"]`);
    buttons.forEach(button => {
      if (isActive) {
        button.classList.add('active');
        button.setAttribute('aria-pressed', 'true');
        button.setAttribute('title', 'Remove from wishlist');
      } else {
        button.classList.remove('active');
        button.setAttribute('aria-pressed', 'false');
        button.setAttribute('title', 'Add to wishlist');
      }
    });
  }
}

/**
 * Wishlist Grid Web Component
 */
class WishlistGrid extends HTMLElement {
  constructor() {
    super();
    this.gridContainer = this.querySelector('[data-wishlist-grid]');
    this.emptyState = this.querySelector('[data-wishlist-empty]');
    this.loading = false;
  }

  connectedCallback() {
    // Wait for storage to be available
    WishlistStorage.ensureStorageReady(() => {
      this.initializeGrid();
      document.addEventListener('wishlist:product-removed', this.handleProductRemoved.bind(this));
      
      // Merge wishlists if on account page
      const config = window.wishlistConfig || {};
      if (config.template === 'customers/account') {
        this.mergeWishlists();
      }
    });
  }

  disconnectedCallback() {
    document.removeEventListener('wishlist:product-removed', this.handleProductRemoved.bind(this));
  }


  async initializeGrid() {
    if (this.loading || !this.gridContainer || !window.wishlistStorage) return;

    const wishlist = window.wishlistStorage.getWishlist();
    
    if (wishlist.length === 0) {
      this.showEmptyState();
      return;
    }

    await this.loadProducts(wishlist);
  }

  async mergeWishlists() {
    try {
      const mergedWishlist = await window.wishlistStorage.mergeWishlist();
      if (mergedWishlist.length > 0) {
        await this.loadProducts(mergedWishlist);
      } else {
        this.showEmptyState();
      }
    } catch (error) {
      console.error('[Wishlist Grid] Error merging wishlists:', error);
      this.initializeGrid();
    }
  }

  async loadProducts(productIds) {
    if (this.loading) return;
    
    this.loading = true;
    this.hideEmptyState();

    try {
      const queryString = window.wishlistStorage.getSearchQueryString(productIds);
      const config = window.wishlistConfig || {};
      const searchUrl = `${config.searchUrl || '/search'}?view=wishlist&type=product&q=${encodeURIComponent(queryString)}`;
      
      const response = await fetch(searchUrl);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      
      const html = await response.text();
      
      if (this.gridContainer) {
        this.gridContainer.innerHTML = html;
        this.initializeLoadedProducts();
        
        if (this.gridContainer.children.length > 0) {
          this.showGrid();
        } else {
          this.showEmptyState();
        }
      }
      
    } catch (error) {
      console.error('[Wishlist Grid] Error loading products:', error);
      this.showEmptyState();
    } finally {
      this.loading = false;
    }
  }

  initializeLoadedProducts() {
    // Re-initialize wishlist buttons in loaded products
    WishlistStorage.initializePageElements();

    // Initialize other functionality if available
    if (window.theme?.QuickShop) {
      new window.theme.QuickShop(this.gridContainer);
    }
    
    if (window.theme?.QuickAdd) {
      new window.theme.QuickAdd(this.gridContainer);
    }

    document.dispatchEvent(new CustomEvent('wishlist:products-loaded', {
      detail: { container: this.gridContainer }
    }));
  }

  handleProductRemoved(event) {
    const productId = event.detail.productId;
    const productCards = this.gridContainer?.querySelectorAll(`[data-product-id="${productId}"]`);
    productCards?.forEach(card => {
      const productCard = card.closest('.product-card, .card-product, [data-product-card]');
      if (productCard) productCard.remove();
    });
    
    if (this.gridContainer && this.gridContainer.children.length === 0) {
      this.showEmptyState();
    }
  }

  showEmptyState() {
    if (this.emptyState) this.emptyState.style.display = 'block';
    if (this.gridContainer) this.gridContainer.style.display = 'none';
  }

  hideEmptyState() {
    if (this.emptyState) this.emptyState.style.display = 'none';
  }

  showGrid() {
    if (this.gridContainer) this.gridContainer.style.display = '';
  }

  async clearAll() {
    try {
      await window.wishlistStorage.clearWishlist();
      WishlistStorage.updateCounter(0);
      this.showEmptyState();
      document.dispatchEvent(new CustomEvent('wishlist:cleared'));
    } catch (error) {
      console.error('[Wishlist Grid] Error clearing wishlist:', error);
    }
  }
}

// Register web components
if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

if (!customElements.get('wishlist-grid')) {
  customElements.define('wishlist-grid', WishlistGrid);
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', WishlistStorage.initialize);
} else {
  WishlistStorage.initialize();
}

// Re-initialize when sections are reloaded (Shopify theme editor)
document.addEventListener('shopify:section:load', WishlistStorage.initializePageElements);